<template>
    <div class="sticky top-0 flex flex-col lg:flex-row justify-between items-start">
        <h2 class="text-xl leading-9 bg-gradient-to-r from-bhav-cool-gray-900 to-bhav-cool-gray-600 text-transparent bg-clip-text font-medium font-headline antialiased ">Info</h2>
        
        <nav v-if="user.level >= 2" class="flex items-end gap-2">
            <FormButton @click="edit()" v-if="!editing" icon="fa-pen" icon-size="text-lg" prefix-class="fak" color="blue">Edit</FormButton>
            <template v-else>
                <FormButton @click="cancel()" color="gray">Cancel</FormButton>
                <FormButton @click="save" color="green">Save Changes</FormButton>
            </template>
        </nav>

    </div>
    <p class="text-sm text-bhav-cool-gray-500 bg-bhav-cool-gray-100 p-2 mt-4 rounded">
        <span class="fak fa-alert mr-2"></span>
        This data is inherited from Kova. Any changes made here will not update Kova, but will override those values.
    </p>
    <dl class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-if="!editing">
        <dt class="font-normal text-bhav-blue-200 py-4 pr-4">Collection Name</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal">{{ collection.meta?.info?.name }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Slug</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ collection.slug }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Tagline</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ collection.meta?.info?.tagline }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Description</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ collection.meta?.info?.description }}</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Interest Rate</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50">{{ collection.meta?.info?.interest_rate ?? (collection.community?.meta?.info?.interest_rate ?? 5) }}%</dd>

        <dt class="font-normal text-bhav-blue-200 py-4 pr-4 border-t-[2px] border-bhav-cool-gray-50">Collection Website</dt>
        <dd class="text-bhav-blue-500 lg:col-span-4 lg:py-4 pr-4 font-normal border-t-[2px] border-bhav-cool-gray-50"><a class="text-bhav-blue-500 hover:underline" :href="collection.meta.info.website" target="_blank" title="Visit this home's website">{{ collection.meta.info.website }}</a></dd>
    </dl>

    <form @submit="save" class="grid grid-cols-1 lg:grid-cols-5 mt-2 rounded-lg bg-white px-4" v-else-if="user.level >= 2">
        <legend class="sr-only">Custom Properties</legend>

        <label for="info.name" class="font-normal text-bhav-blue-300 py-5">Collection Name</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 w-full">
            <TextInput id="info.name" v-model="item.meta.info.name" maxlength="60" size="60" />
        </div>

        <label for="slug" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Slug</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <TextInput id="slug" v-model="item.slug" @keyup="validateSlug" maxlength="60" size="60" />
        </div>

        <label for="slug" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Tagline</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <TextInput id="tagline" v-model="item.meta.info.tagline" maxlength="255" size="255" />
        </div>

        <label for="info.description" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Description</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <textarea class="bg-bhav-cool-gray-50 text-bhav-blue-500 text-sm w-full p-3 rounded-lg focus:border-bhav-blue-400 min-h-48" id="info.description" v-model="item.meta.info.description"></textarea>
        </div>

        <label for="info.interest_rate" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Interest Rate</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <NumberField suffix="%" id="info.interest_rate" class="max-w-32" :max="100" :min="0" step=".01" v-model="item.meta.info.interest_rate" />
        </div>

        <label for="info.website" class="font-normal text-bhav-blue-300 py-5 border-t-[2px] border-bhav-cool-gray-50">Collection Website</label>
        <div class="text-oakwood-gray-900 lg:col-span-4 py-5 border-t-[2px] border-bhav-cool-gray-50 w-full">
            <URLField id="info.website" v-model="item.meta.info.website" width="w-full" />
        </div>

    </form>
       
</template>

<script>
import { formatToCurrency } from '@/helpers/numberHelpers';
import {FormButton, URLField, SelectField, TextInput} from '@/components/form';
import {states} from '@/helpers/globals';
import { mapState } from 'pinia';
import { useAuthStore } from '@/stores/auth';
import NumberField from '../form/NumberField.vue';

export default {
    data() {
        return {
            item: {},
            states,
            editing: false,
        }
    },
    methods: {
        edit() {
            if(this.user.level < 2) return;
            this.editing = true;
            this.item = {...this.collection}
        },
        cancel() {
            this.editing = false;
        },
        save() {
            if(this.user.level < 2) return;
            if(this.item.meta?.info?.interest_rate === 0) this.item.meta.info.interest_rate = null;
            this.$emit('update', this.item);
            this.cancel();
        },
        formatToCurrency,
        validateSlug(slug) {
            let clean = slug.target.value.replace(/[^A-Za-z0-9-]/,'');
            slug.target.value = clean;
        }
    },
    computed: {
        ...mapState(useAuthStore, ['user'])
    },
    emits: [
        'update'
    ],
    props: {
        collection: {
            type: Object,
            required: true
        }
    },
    components: {
        FormButton,
        URLField,
        SelectField,
        TextInput,
        NumberField
    }
}
</script>@/components/form